import { useEffect, useState, useCallback, useRef } from 'react';
import { ChatInterface } from '@/components/ChatInterface';
import { Header } from '@/components/Header';
import { Sidebar, type AppView } from '@/components/Sidebar';
import { HabitDashboard } from '@/components/HabitDashboard';
import { HomeDashboard } from '@/components/HomeDashboard';
import { AdminPage } from '@/pages/AdminPage';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { OnboardingFlow } from '@/components/OnboardingFlow';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/hooks/useChat';
import { useSessions } from '@/hooks/useSessions';
import { useHabits } from '@/hooks/useHabits';
import { useGamification } from '@/hooks/useGamification';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { ThemeManager } from '@/lib/theme-manager';
import { getMessages } from '@/lib/chat-api';
import { getOnboardingStatus } from '@/lib/onboarding-api';
type AuthPage = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'pending-approval';

function App() {
  const { user, isLoading: authLoading, isDemo, signOut, checkAuth } = useAuth();
  const [authPage, setAuthPage] = useState<AuthPage>('login');

  // Initialize theme on mount (public endpoint, works before auth)
  useEffect(() => {
    const themeManager = ThemeManager.getInstance();
    themeManager.loadTheme().catch(err => console.error('Theme load error:', err));
  }, []);

  // Check for reset token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reset_token')) {
      setAuthPage('reset-password');
    }
  }, []);

  // Show loading spinner during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen bg-charcoal-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If not authenticated and not demo mode, show auth pages
  if (!user && !isDemo) {
    switch (authPage) {
      case 'login':
        return <LoginPage onNavigate={setAuthPage} onLoginSuccess={() => checkAuth()} />;
      case 'signup':
        return <SignupPage onNavigate={setAuthPage} onSignupSuccess={() => checkAuth()} />;
      case 'forgot-password':
        return <ForgotPasswordPage onNavigate={setAuthPage} />;
      case 'reset-password':
        return <ResetPasswordPage onNavigate={setAuthPage} />;
      case 'pending-approval':
        return <PendingApprovalPage onNavigate={setAuthPage} />;
    }
  }

  // Authenticated (or demo mode) — render main app
  return <AuthenticatedApp user={user} onLogout={signOut} />;
}

interface AuthenticatedAppProps {
  user: { id: string; email: string } | null;
  onLogout: () => void;
}

function AuthenticatedApp({ user, onLogout }: AuthenticatedAppProps) {
  const { messages, isLoading, error, session, sendMessage, startNewSession, clearError } =
    useChat();
  const {
    sessions,
    selectedCategory,
    setSelectedCategory,
    deleteSession,
    refreshSessions,
  } = useSessions();
  const { todayProgress } = useHabits();
  const { stats, levelProgress } = useGamification();
  const { isAdmin } = useAdminAuth();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loadedMessages, setLoadedMessages] = useState(messages);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<AppView>('home');
  const [adminViewMode, setAdminViewMode] = useState<'admin' | 'user'>('admin');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const skipNextRefreshRef = useRef(false);

  // Check onboarding status on mount
  useEffect(() => {
    async function checkOnboarding() {
      if (!user) return;

      try {
        const status = await getOnboardingStatus();
        setShowOnboarding(!status.completed);
      } catch (err) {
        console.error('Failed to check onboarding status:', err);
      } finally {
        setOnboardingChecked(true);
      }
    }

    checkOnboarding();
  }, [user]);

  // Sync messages with loaded session
  useEffect(() => {
    if (session) {
      setActiveSessionId(session.id);
      setLoadedMessages(messages);
    }
  }, [session, messages]);

  // Auto-start a session when app loads
  useEffect(() => {
    if (user && !session) {
      startNewSession();
    }
  }, [user, session, startNewSession]);

  // Refresh sessions when a new session is created (skip after delete to avoid race condition)
  useEffect(() => {
    if (session) {
      if (skipNextRefreshRef.current) {
        skipNextRefreshRef.current = false;
        return;
      }
      refreshSessions();
    }
  }, [session, refreshSessions]);

  const handleSelectSession = useCallback(async (sessionId: string) => {
    if (sessionId === activeSessionId) return;

    try {
      const sessionMessages = await getMessages(sessionId);
      setLoadedMessages(sessionMessages);
      setActiveSessionId(sessionId);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  }, [activeSessionId]);

  const handleNewChat = useCallback(async () => {
    await startNewSession();
  }, [startNewSession]);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    await deleteSession(sessionId);
    if (sessionId === activeSessionId) {
      skipNextRefreshRef.current = true;
      await startNewSession();
    }
  }, [deleteSession, activeSessionId, startNewSession]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (activeSessionId === session?.id) {
      await sendMessage(content);
    } else {
      await sendMessage(content);
    }
    refreshSessions();
  }, [activeSessionId, session, sendMessage, refreshSessions]);

  return (
    <>
      {/* Show onboarding modal if not completed */}
      {showOnboarding && onboardingChecked && (
        <OnboardingFlow
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
        />
      )}

      <div className="min-h-screen flex flex-col">
        <Header user={user} onLogout={onLogout} adminViewMode={isAdmin ? adminViewMode : undefined} />
        <div className="flex-1 flex overflow-hidden">
        <Sidebar
          sessions={sessions}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          activeSessionId={activeSessionId}
          onSelectSession={handleSelectSession}
          onNewChat={handleNewChat}
          onDeleteSession={handleDeleteSession}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeView={activeView}
          onViewChange={setActiveView}
          habitStats={stats ? {
            todayCompleted: todayProgress.completed,
            todayTotal: todayProgress.total,
            level: stats.currentLevel,
            levelProgress: levelProgress,
          } : undefined}
          isAdmin={isAdmin && adminViewMode === 'admin'}
          onToggleAdminView={isAdmin ? () => setAdminViewMode(m => m === 'admin' ? 'user' : 'admin') : undefined}
          adminViewMode={isAdmin ? adminViewMode : undefined}
        />
        <main className="flex-1 p-4 overflow-hidden">
          {activeView === 'home' ? (
            <HomeDashboard
              sessions={sessions}
              onNavigateToChat={() => setActiveView('chat')}
              onNavigateToHabits={() => setActiveView('habits')}
              onStartNewChat={async () => {
                await handleNewChat();
                setActiveView('chat');
              }}
              onSelectSession={handleSelectSession}
            />
          ) : activeView === 'chat' ? (
            <ChatInterface
              messages={activeSessionId === session?.id ? messages : loadedMessages}
              onSendMessage={handleSendMessage}
              onStartNewSession={startNewSession}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          ) : activeView === 'habits' ? (
            <HabitDashboard />
          ) : activeView === 'admin' && adminViewMode === 'admin' ? (
            <AdminPage />
          ) : null}
        </main>
        </div>
      </div>
    </>
  );
}

export default App;
